import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const BOTH_WAYS_FIGURE = (
  <Figure
    title="Both directions, one folder"
    caption="Revvly files what it holds into the company folder, and brings back what people add there. Files Revvly wrote are never brought back."
  >
    <Flow
      steps={[
        {
          fork: [
            {
              title: 'Revvly to Drive',
              text: 'Invoices, credit notes, uploads, email, form and Support files.',
            },
            {
              title: 'Drive to Revvly',
              text: 'Files people add appear on the Documents tab.',
            },
          ],
        },
        { title: 'Mark a file', text: 'Invoice, Credit note, Contract and more.', tone: 'muted' },
        {
          title: 'Import review',
          text: 'Invoice and Credit note marks are checked before anything is billed.',
          tone: 'accent',
        },
        { title: 'Record payment', text: 'Against the imported invoice, from the same row.' },
      ]}
    />
  </Figure>
);

const STEPS: {
  title: string;
  figure?: ReactNode;
  body: string;
  entity?: string;
  entityLabel?: string;
  where?: [string, ...string[]];
  whereNote?: string;
}[] = [
  {
    title: 'A Drive folder for every client and prospect',
    body: 'Client folders give each client, and each prospect when the CRM add-on is on, its own folder in your Google Drive, under a Clients folder and a Prospects folder you choose. Revvly files what it holds about that company into it: invoices and credit notes, files uploaded on the record, email attachments, form uploads and Support attachments. Files your team adds to the folder in Drive come back the other way and show up on the company’s Documents tab. Client folders are switched on for a workspace by Revvly; ask us if the Client folders card is not shown under Settings, Integrations.',
    figure: BOTH_WAYS_FIGURE,
  },
  {
    title: 'Give Revvly write access',
    body: 'Connecting Google Drive for the invoice import gives Revvly read-only access. Client folders need permission to create folders and write files, so the Client folders card first shows Enable client folders, which sends you to Google to grant it. Revvly only writes inside the folders you choose and never changes sharing. The card’s status then reads Running once the switch is on, or Off, Paused – Google Drive disconnected, Paused – write access missing, or Reconnect needed when Google holds only read-only access.',
    where: ['Settings', 'Integrations', 'Client folders'],
    whereNote:
      'The card appears once Google Drive is connected and client folders are switched on for your workspace. Only admins change it.',
  },
  {
    title: 'Choose the root folders and how folders are named',
    body: 'Under Root folders, choose the Clients folder, and the Prospects folder if you use the CRM. We recommend a shared drive, and keeping both roots in the same one: folders in someone’s My Drive are lost if their account is deleted, and Google may refuse to move a folder between two drives when a prospect becomes a client. Folder name sets the pattern for new folders, with {name}, {externalId} or {invoicePrefix}; the card shows an example as you type. Layout is Subfolders by source (Invoices, Email attachments, Documents, Forms and Support, each renameable) or Everything in one folder. Changing a root later does not move folders that already exist; they stay where they are and keep syncing.',
    where: ['Settings', 'Integrations', 'Client folders', 'Root folders'],
  },
  {
    title: 'Pick what is filed, and what comes back',
    body: 'What is filed lists the sources Revvly copies into each folder: Invoices and credit notes, Uploads, Email attachments, Form uploads and Support attachments. Only files Revvly has already checked are copied, and Support attachments only from replies the customer could see. An email attachment from a thread that is private when the copy is made is never copied. Under From Drive, Pull files added in Drive brings files your team adds in a company folder onto its Documents tab. With subfolders, Invoices subfolder means Invoice marks every file dropped in the Invoices subfolder as an invoice. Import from client folders decides what happens to invoice files: When marked as invoice sends files marked Invoice or Credit note to the import review, Suggest likely invoices also flags files that look like invoices for someone to confirm, and Off imports nothing.',
    where: ['Settings', 'Integrations', 'Client folders', 'From Drive'],
    whereNote:
      'Google Docs, Sheets and Slides, and files over 100 MB, are listed with a link to Drive rather than copied.',
  },
  {
    title: 'Set the policies, then turn it on',
    body: 'New records decides whether a new client or prospect gets a folder: Create a folder automatically, Ask on the record (the record offers Create folder, Link existing or Not now) or Manual. When a prospect becomes a client chooses Move folder automatically, Ask, or Leave in place for a folder in the Prospects root. Folders that live elsewhere covers a converted prospect whose folder sits outside the Prospects folder: Ask, Always move or Never move. Save, then turn on Sync client folders. Everything is a draft until you save, so you can set both roots and the name pattern before Revvly writes anything.',
    where: ['Settings', 'Integrations', 'Client folders', 'Policies'],
  },
  {
    title: 'Map the companies you already have',
    body: 'Folder mapping and activity lists every client and prospect with its folder, its state and where the folder sits. Filter by Clients only or Prospects only, and by With a folder, No folder or Needs attention. Where your Drive already has a folder for a company, Revvly suggests it: one the invoice import used, an exact name match, or a similar name. Accept all exact matches takes the safe ones in one go, and Create folders for all unmapped makes the rest. A folder belongs to at most one company, so a shared folder that holds every client’s invoices is never linked to one of them. Linking or creating a folder for a company with history files that history too.',
    where: ['Settings', 'Integrations', 'Client folders', 'Folder mapping and activity'],
    whereNote:
      'The Activity tab lists each file Revvly keeps in a client folder and its status: Synced, Pending, Failed, Removed in Drive, Skipped, Cancelled or Removed in Revvly. Retry a failed file, Restore one someone removed in Drive, or Run check now to pick up anything missed.',
  },
  {
    title: 'The folder bar on a record',
    body: 'At the top of a client’s or prospect’s Documents tab, the folder bar shows the folder’s state (Synced, Not synced, Creating, Folder missing, Paused or Error), its name, Open in Drive, and counts of synced, pending and failed files. The Folder menu offers Create folder, Link existing folder, Change folder, Move to Clients folder and Stop syncing. Stop syncing leaves the folder and everything in it in Google Drive. If a folder could not be moved when the prospect became a client, the bar says why, with Retry.',
    where: ['Clients', 'open a client', 'Documents'],
    whereNote:
      'Without the CRM add-on, the Documents tab shows only the folder bar; the list of files and marks needs CRM.',
    entity: 'client',
    entityLabel: 'Client',
  },
  {
    title: 'Mark files',
    body: 'Each file on the Documents tab can carry a mark: Invoice, Credit note, Contract, Proposal, Deck, Proof of payment or Other. Filter the list by source (From Google Drive among them) and by mark, including Invoices not yet imported and Imported, unpaid. An invoice mark moves through Marked, In review, Imported, Partly paid and Paid. With Suggest likely invoices on, a likely file shows Looks like an invoice with Confirm and Not an invoice. An Imported mark is locked; to undo it, roll back the import batch. Revvly shows the mark and payment state in Drive too, as one line starting with Revvly: in the file’s description, for example the invoice number and Paid with its date. The rest of the description, the file name and its contents are left alone.',
    where: ['Clients', 'open a client', 'Documents', 'Mark'],
  },
  {
    title: 'Import marked invoices and record payments',
    body: 'Files marked Invoice or Credit note go through the same review as a Google Drive import: extracted, checked by a person, then committed one client at a time. Email attachments filed on a client can be marked and imported the same way; an attachment from a private thread is never imported. When the import is waiting for review, the bell tells the person who started it, or the workspace admins for an import Revvly started from client folders. Once a file is imported, Record payment on its row books a payment against that invoice, with a date, an amount (or Full balance) and an optional proof of payment from the client’s files. A credit note takes no payment here; it is applied on the invoice page.',
    where: ['Clients', 'open a client', 'Documents', 'Record payment'],
    whereNote:
      'A transaction import whose row carries a payment date also settles an invoice Revvly already holds. See Import historical invoices.',
    entity: 'invoice',
    entityLabel: 'Invoice',
  },
  {
    title: 'Attach Drive files to an email',
    body: 'The email composer has a Drive button, on a client’s Conversation tab, in the CRM contact email panel and when you reply from the Work Inbox. It opens in the record’s own folder, shows the path across the top with clickable breadcrumbs, and lets you click into subfolders; searching looks by file name across every synced record folder. Browsing and attaching stay inside the Clients and Prospects folders and the active record folders, so the picker does not reach the rest of your Drive. Revvly attaches a private copy taken when you pick the file, so a later change in Drive cannot break a draft; a Google Doc, Sheet or Slides file goes out as a PDF. The usual limit of 10 MB per attachment applies.',
    where: ['Clients', 'open a client', 'Conversation', 'Drive'],
  },
  {
    title: 'What Revvly will not do',
    body: 'Revvly never deletes, trashes or re-shares anything in your Drive, and files Revvly wrote are never imported back as invoices. Converting a prospect, issuing an invoice or filing an email never fails or waits because of Drive; the Drive work happens shortly afterwards, and a regular check catches anything that was missed. If Google stops accepting the connection, client folders pause and admins get a Google Drive client folders stopped syncing notification. Reconnect Google Drive in Integrations and the files queued in the meantime are synced. A file someone removes in Drive is hidden on the record, and Revvly keeps its own copy for 30 days.',
  },
];

export function DriveFoldersGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Keep client folders in Google Drive — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Client folders in Google Drive</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Keep client folders in <em>Google Drive</em>.
        </h1>
        <p className="dp-oneliner">
          A Drive folder for every client and prospect, kept in step both ways. Revvly files
          invoices and attachments into it, files your team adds come back to the record, and files
          marked as invoices go through the import review.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
          {step.entity && (
            <ul className="dp-connects" style={{ marginTop: 14 }}>
              <li>
                <Link
                  to={`/docs/concepts/${step.entity}`}
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  {step.entityLabel} concept ›
                </Link>
              </li>
            </ul>
          )}
        </div>
      ))}

      <ul className="dp-connects">
        <li>
          <Link
            to="/docs/guides/import-invoices"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Import historical invoices ›
          </Link>
        </li>
        <li>
          <Link
            to="/docs/reference/integrations"
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Integrations and availability ›
          </Link>
        </li>
      </ul>
    </div>
  );
}
